import type { Campaign } from '../campaigns/model';
import type { Task } from '../../types';

export interface CampaignVisibilityContext {
  isAdmin: boolean;
  reviewCampaignIds: string[];
  expertId: string | null;
}

export interface ClaimVisibilityContext {
  isAdmin: boolean;
  expertId: string | null;
}

export interface WriterCandidate {
  ownerId: string;
  ownerName: string | null;
  expertId: string | null;
  taskCount: number;
}

/** Select campaigns a caller may continue to see in the queue. */
export function selectVisibleCampaigns(
  allCampaigns: Campaign[],
  myOpenTasks: Task[],
  context: CampaignVisibilityContext,
): Campaign[] {
  const campaignsWithMyOpenWork = new Set(myOpenTasks.map((task) => task.campaignId));
  return allCampaigns.filter((campaign) => {
    const hasOpenWork = campaignsWithMyOpenWork.has(campaign.id);
    if (campaign.archivedAt) return hasOpenWork;
    if (context.isAdmin) return true;
    if (context.reviewCampaignIds.includes(campaign.id)) return true;
    if (context.expertId !== null && campaign.activeExpertIds.includes(context.expertId)) return true;
    return hasOpenWork;
  });
}

/** Claiming is available only to admins or active experts on live campaigns. */
export function canClaimCampaign(
  campaign: Campaign | null,
  context: ClaimVisibilityContext,
): boolean {
  if (!campaign || campaign.archivedAt) return false;
  if (context.isAdmin) return true;
  return context.expertId !== null && campaign.activeExpertIds.includes(context.expertId);
}

/**
 * Build reviewer writer-filter candidates from the already-loaded queue
 * streams.  Keeping this reducer pure prevents the desktop and mobile views
 * from inventing different filtering policy.
 */
export function selectWriterCandidates(
  streams: Task[][],
  selectedCampaignId: string | null,
): WriterCandidate[] {
  if (!selectedCampaignId) return [];
  const byOwnerId = new Map<string, WriterCandidate>();
  for (const stream of streams) {
    for (const task of stream) {
      if (!task.ownerId || task.campaignId !== selectedCampaignId) continue;
      const existing = byOwnerId.get(task.ownerId);
      if (existing) {
        existing.taskCount += 1;
        if (!existing.ownerName && task.ownerName) existing.ownerName = task.ownerName;
        if (!existing.expertId && task.expertId) existing.expertId = task.expertId;
      } else {
        byOwnerId.set(task.ownerId, {
          ownerId: task.ownerId,
          ownerName: task.ownerName,
          expertId: task.expertId,
          taskCount: 1,
        });
      }
    }
  }
  return Array.from(byOwnerId.values()).sort((a, b) => {
    if (b.taskCount !== a.taskCount) return b.taskCount - a.taskCount;
    return (a.ownerName ?? '').localeCompare(b.ownerName ?? '');
  });
}
